export function createNavHeightObserver({ isOverlay, getSectionEl, onHeight, onClear }) {
    let resizeObserver = null;
    let frameId = null;
    let timerId = null;
    const resolveHeight = (sectionEl, entryHeight) => {
        const measuredHeight = entryHeight || sectionEl?.offsetHeight || 0;
        if (typeof window === "undefined" || window.innerWidth > 768) {
            return measuredHeight;
        }

        const navbarEl = sectionEl?.querySelector?.(".navbar");
        const headerHeight = sectionEl?.querySelector?.(".navbar-header")?.offsetHeight;
        // I had no better idea. Damn backdrop-blur fucks everything up.
        if (navbarEl && headerHeight) {
            const styles = window.getComputedStyle(navbarEl);
            const paddingTop = parseFloat(styles.paddingTop) || 0;
            const paddingBottom = parseFloat(styles.paddingBottom) || 0;
            const marginTop = parseFloat(styles.marginTop) || 0;
            const marginBottom = parseFloat(styles.marginBottom) || 0;
            const borderTop = parseFloat(styles.borderTopWidth) || 0;
            const borderBottom = parseFloat(styles.borderBottomWidth) || 0;
            return Math.round(headerHeight + paddingTop + paddingBottom + marginTop + marginBottom + borderTop + borderBottom);
        }
        return headerHeight || measuredHeight;
    };

    const teardown = () => {
        if (resizeObserver) {
            resizeObserver.disconnect();
            resizeObserver = null;
        }
        if (frameId) {
            if (typeof window !== "undefined" && typeof window.cancelAnimationFrame === "function") {
                window.cancelAnimationFrame(frameId);
            }
            frameId = null;
        }
        if (timerId) {
            clearTimeout(timerId);
            timerId = null;
        }
    };

    const setup = () => {
        teardown();
        if (!isOverlay()) {
            onClear?.();
            return;
        }
        const sectionEl = getSectionEl();
        if (!sectionEl) return;
        const ResizeObserverRef = window?.ResizeObserver || globalThis.ResizeObserver;
        if (!ResizeObserverRef) return;

        resizeObserver = new ResizeObserverRef((entries) => {
            const entry = entries[0];
            const height = resolveHeight(sectionEl, entry?.contentRect?.height);
            onHeight(height);
        });
        resizeObserver.observe(sectionEl);
        onHeight(resolveHeight(sectionEl));
        // Route/component mount order can delay first content section render.
        if (typeof window !== "undefined" && typeof window.requestAnimationFrame === "function") {
            frameId = window.requestAnimationFrame(() => onHeight(resolveHeight(sectionEl)));
        }
        timerId = setTimeout(() => onHeight(resolveHeight(sectionEl)), 80);
    };

    return { setup, teardown };
}

function getDefaultTargets() {
    const heroSection = document?.querySelector?.(".ufo-sec--hero");
    const mainHeaderSection = document?.querySelector?.(".ufo-sec--header-main");
    const navigationSectionContainer = document?.querySelector?.(".ufo--page-sections .section-container:has(.ufo-sec--navigation.nav-overlay)");
    const firstAddSectionRow = document?.querySelector?.(".ufo--page-sections.editor .ufo--add-section-row");
    const firstContentSection = document?.querySelector?.(
        ".ufo--page-sections .ufo-sec:not(.ufo-sec--navigation):not(.ufo-sec--hero):not(.ufo-sec--header-main)"
    );
    const firstVisualContent = [firstAddSectionRow, firstContentSection]
        .filter(Boolean)
        .sort((a, b) => {
            if (a === b) return 0;
            const relation = a.compareDocumentPosition(b);
            const nodeRef = window?.Node || globalThis.Node;
            return relation & nodeRef.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
        })[0];

    return Array.from(new Set([
        heroSection,
        mainHeaderSection,
        !heroSection && !mainHeaderSection ? (navigationSectionContainer || firstVisualContent) : null,
    ].filter(Boolean)));
}

export function setNavHeightVars({ height, isBelow, isOverlay, targetEl } = {}) {
    const targets = targetEl ? [targetEl] : getDefaultTargets();
    if (!targets.length) return;
    if (!height) {
        targets.forEach((target) => {
            target.style.removeProperty("--portfolioNavPaddingTop");
            target.style.removeProperty("--portfolioNavPaddingBottom");
            target.style.removeProperty("--addSectionNavOffsetTop");
            target.style.removeProperty("--addSectionNavOffsetBottom");
        });
        return;
    }
    const value = `${Math.round(height)}px`;
    targets.forEach((target) => {
        const isAddSectionRow = target.classList?.contains("ufo--add-section-row");
        const topVar = isAddSectionRow ? "--addSectionNavOffsetTop" : "--portfolioNavPaddingTop";
        const bottomVar = isAddSectionRow ? "--addSectionNavOffsetBottom" : "--portfolioNavPaddingBottom";
        if (isBelow) {
            target.style.setProperty(bottomVar, value);
            target.style.removeProperty(topVar);
            return;
        }
        if (isOverlay) {
            target.style.setProperty(topVar, value);
            target.style.removeProperty(bottomVar);
        }
    });
}
